import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
	component: Privacy,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/privacy",
		});

		return {
			meta: [
				{ title: "Privacy Policy - AgentMRR" },
				{
					name: "description",
					content:
						"How AgentMRR handles your data. What we collect, how we use it, and your rights on our AI agent revenue leaderboard.",
				},
				{ property: "og:title", content: "Privacy Policy - AgentMRR" },
				{
					property: "og:description",
					content: "How AgentMRR protects your privacy and handles your data.",
				},
				{
					property: "og:image",
					content: ogImageUrl("privacy"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("privacy") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/privacy",
			}),
		};
	},
});

function PrivacySection({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section className="space-y-3 scroll-mt-24">
			<h2 className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
				&gt; {title}
			</h2>
			<div className="space-y-3 text-base leading-[1.65] text-muted-foreground">
				{children}
			</div>
		</section>
	);
}

function Privacy() {
	const lastUpdated = "May 2026";

	const rights = [
		"Access and download your data",
		"Correct inaccurate information on your profile",
		"Delete your account and agent listings",
		"Disconnect your revenue-provider account at any time",
		"Opt out of non-essential communications",
		"Request information about data processing",
		"Request removal of your agent from the leaderboard",
	];

	return (
		<div className="min-h-svh bg-background text-foreground">
			<PublicHeader />
			<div className="w-full px-4 sm:px-6 xl:px-[392px]">
				<main className="mx-auto w-full max-w-5xl pt-28 pb-24 sm:pt-32">
					<div className="space-y-10">
						<div className="space-y-3 border-b border-border pb-6">
							<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Legal / privacy
							</p>
							<h1
								className="font-mono text-[clamp(1.5rem,3vw,2rem)] leading-[1.1] tracking-[-0.02em]"
								style={{ fontWeight: 400 }}
							>
								<span className="text-brand">/</span>privacy
							</h1>
							<p className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
								Last updated · {lastUpdated}
							</p>
						</div>

						<PrivacySection title="1. Account Information">
							<p>
								When you create an account, we collect your email address, name,
								authentication provider details, session metadata, and any
								profile information you choose to provide. When you submit,
								claim, or update a listing without first creating a full
								account, we still store the minimum internal owner record needed
								to manage the listing securely.
							</p>
						</PrivacySection>

						<PrivacySection title="2. Agent Submission Data">
							<p>
								When you submit an agent to AgentMRR, we collect the agent name,
								website URL, description, category, founder or company name, and
								any logos, screenshots, founder story details, public metrics,
								or social proof you provide. We may also store listing status,
								claim status, website verification state, public badge data, and
								timestamps used to show when a listing was published, updated,
								verified, or synced.
							</p>
						</PrivacySection>

						<PrivacySection title="3. Revenue Verification Data">
							<p>
								When you connect a supported revenue provider, including Stripe,
								Creem, or Paddle, we access read-only subscription, transaction,
								product, and revenue data needed to calculate MRR, total
								revenue, active subscriptions, and verification status for your
								listing. We store provider identifiers, encrypted credentials,
								key fingerprints, key prefixes, sync status, verification
								labels, revenue snapshots, and timestamps needed for ranking,
								verification, freshness checks, and secure access. We do not
								publicly expose full transaction histories, customer lists,
								payout data, or sensitive financial internals.
							</p>
						</PrivacySection>

						<PrivacySection title="4. Usage and Agent Auth Access Data">
							<p>
								We collect information about how you use AgentMRR, including
								pages visited, features used, and interactions with the
								leaderboard and profiles. When agents or developers interact
								through the Agent Auth interface, we log request metadata for
								rate limiting, security, approvals, replay prevention,
								capability management, debugging, and service improvement. This
								may include host and agent public keys, issuer identifiers, key
								thumbprints, capability grants, approval requests, JWT IDs,
								statuses, and last-seen timestamps.
							</p>
						</PrivacySection>

						<PrivacySection title="5. Device Information">
							<p>
								We may collect information about the device you use to access
								AgentMRR, including browser type, operating system, and IP
								address. If you contact us through support email or similar
								support tools, we may store the conversation, contact details,
								attachments, and related metadata needed to respond.
							</p>
						</PrivacySection>

						<PrivacySection title="6. How We Use Your Information">
							<p>We use the information we collect to:</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>
									Verify recurring revenue claims and calculate leaderboard
									rankings
								</li>
								<li>
									Display agent profiles and MRR data publicly on the
									leaderboard
								</li>
								<li>
									Operate submission, claim, account, dashboard, badge, and
									provider-key access flows
								</li>
								<li>
									Provide the Agent Auth interface and process advertising
									inventory
								</li>
								<li>
									Send AgentMRR Radar emails and other notifications you request
									or that are required to operate the Service
								</li>
								<li>Respond to support, feedback, and abuse reports</li>
								<li>
									Improve verification accuracy, product, and overall experience
								</li>
								<li>
									Send service-related emails about listing status and platform
									changes
								</li>
								<li>
									Detect and prevent fraud, ranking manipulation, and other
									abuse
								</li>
							</ul>
						</PrivacySection>

						<PrivacySection title="7. How We Protect Your Data">
							<p>
								All data transmitted between your device and our servers is
								encrypted using industry-standard TLS encryption. We store only
								the provider data necessary for verification, ranking, access
								control, and sync reliability. Revenue-provider credentials are
								stored encrypted, and full sensitive financial internals are not
								displayed publicly. Data is stored on secure infrastructure with
								access controls, and only authorized team members can access
								user data when necessary for support, maintenance, security, or
								abuse prevention.
							</p>
						</PrivacySection>

						<PrivacySection title="8. Data Sharing and Third Parties">
							<p>
								Agent names, descriptions, MRR figures, verification status, and
								submitted public media are displayed publicly on the
								leaderboard, profile pages, category pages, embeddable badges,
								search experiences, Radar emails, and available programmatically
								through public or approved Agent Auth interfaces. This is the
								core function of the Service.
							</p>
							<p>
								We never sell your personal information or revenue-provider data
								to third parties. We work with trusted service providers,
								including hosting, database, authentication, payments,
								analytics, email, file storage, and support providers, who are
								bound by confidentiality or data-protection obligations. We may
								disclose information if required by law, to prevent abuse or
								security incidents, or if AgentMRR is involved in a merger,
								acquisition, financing, or asset transfer.
							</p>
						</PrivacySection>

						<PrivacySection title="Your rights">
							<p>You have the following rights regarding your personal data:</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								{rights.map((right) => (
									<li key={right}>{right}</li>
								))}
							</ul>
						</PrivacySection>

						<PrivacySection title="Cookies">
							<p>
								We use cookies to provide essential functionality and improve
								your experience. These include:
							</p>
							<ul className="list-disc pl-5 space-y-1 text-base text-muted-foreground">
								<li>
									<strong
										className="text-foreground"
										style={{ fontWeight: 400 }}
									>
										Essential:
									</strong>{" "}
									Required for the site to function, including auth and
									security.
								</li>
								<li>
									<strong
										className="text-foreground"
										style={{ fontWeight: 400 }}
									>
										Analytics:
									</strong>{" "}
									Help us understand how you use AgentMRR so we can improve.
								</li>
								<li>
									<strong
										className="text-foreground"
										style={{ fontWeight: 400 }}
									>
										Preferences:
									</strong>{" "}
									Remember your settings.
								</li>
							</ul>
						</PrivacySection>

						<PrivacySection title="Contact">
							<p>If you have any questions, contact us at:</p>
							<p>
								<a
									href="mailto:privacy@agentmrr.com"
									className="font-mono text-brand transition-colors hover:text-brand-hover"
								>
									privacy@agentmrr.com
								</a>
							</p>
						</PrivacySection>
					</div>

					<div className="mt-12 border-t border-border pt-6">
						<Link
							to="/"
							search={{ unsubscribed: undefined }}
							className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
						>
							<ArrowLeft className="size-3.5" />
							Back to home
						</Link>
					</div>
				</main>
			</div>
			<PublicFooter />
		</div>
	);
}
